import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ShoppingCart, 
  TrendingDown, 
  TrendingUp,
  Clock, 
  CheckCircle, 
  XCircle, 
  Target,
  ArrowLeft,
  BookOpen,
  Building2,
  PlayCircle,
  Search,
  Filter,
  Plus,
  AlertCircle,
  Link,
  MoreVertical,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { LogoutButton } from '@/components/LogoutButton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DemoData = {
  waitlistItems: [
    {
      id: 1,
      name: 'MacBook Pro 14" M3',
      price: 2499,
      category: 'Business',
      status: 'waiting',
      url: 'https://apple.com',
      created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 2,
      name: 'Standing Desk',
      price: 599,
      category: 'Business',
      status: 'waiting',
      url: 'https://example.com',
      created_at: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 3,
      name: 'Gaming Chair',
      price: 399,
      category: 'Nice-to-Have',
      status: 'waiting',
      url: 'https://example.com',
      created_at: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 4,
      name: 'Smartwatch',
      price: 279,
      category: 'Nice-to-Have',
      status: 'skipped',
      created_at: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 5,
      name: 'Noise Cancelling Headphones',
      price: 299,
      category: 'Business',
      status: 'bought',
      created_at: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString()
    }
  ],
  stats: {
    totalSaved: 279,
    itemsInWaitlist: 3,
    itemsSkipped: 1,
    itemsBought: 1
  }
};

const Demo = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('waiting');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [demoItems, setDemoItems] = useState(DemoData.waitlistItems); // Demo-Daten als State

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Business': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Nice-to-Have': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'Persönlich': return 'bg-green-100 text-green-800 border-green-200';
      case 'Sonstiges': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'Business': 
        return { 
          name: 'Business', 
          description: 'Berufliche Anschaffungen und Investitionen',
          icon: '💼'
        };
      case 'Nice-to-Have': 
        return { 
          name: 'Nice-to-Have', 
          description: 'Wünschenswerte aber nicht notwendige Artikel',
          icon: '🎯'
        };
      case 'Persönlich': 
        return { 
          name: 'Persönlich', 
          description: 'Private Anschaffungen und Hobbys',
          icon: '🏠'
        };
      case 'Sonstiges': 
        return { 
          name: 'Sonstiges', 
          description: 'Andere Kategorien',
          icon: '📦'
        };
      default: 
        return { 
          name: category, 
          description: 'Unbekannte Kategorie',
          icon: '❓'
        };
    }
  };

  const formatTimeLeft = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = now.getTime() - created.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, 30 - diffDays);
    
    if (remainingDays === 0) {
      return 'Wartezeit beendet';
    } else if (remainingDays === 1) {
      return '1 Tag übrig';
    } else {
      return `${remainingDays} Tage übrig`;
    }
  };

  const getProgressPercentage = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = now.getTime() - created.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return Math.min((diffDays / 30) * 100, 100);
  };

  const filteredItems = demoItems.filter(item => {
    // Tab-Filter
    if (activeTab !== 'all' && activeTab !== 'subscriptions' && item.status !== activeTab) return false;
    
    // Such-Filter
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Kategorie-Filter
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
    
    return true;
  });

  const handleStatusChange = (itemId: string, newStatus: 'bought' | 'skipped') => {
    setDemoItems(prevItems => 
      prevItems.map(item => 
        item.id.toString() === itemId 
          ? { ...item, status: newStatus, decision_date: new Date().toISOString() }
          : item
      )
    );
    
    // Modernes Toast-Feedback
    if (newStatus === 'skipped') {
      toast.success('Impulskauf erfolgreich vermieden!', {
        description: 'Gut gemacht! Du hast Geld gespart und deine Selbstkontrolle gestärkt.',
        duration: 4000,
      });
    } else {
      toast.info('Produkt als gekauft markiert', {
        description: 'Das Produkt wurde zu deinen Käufen hinzugefügt.',
        duration: 3000,
      });
    }
  };

  const getStatusCounts = () => {
    const waiting = demoItems.filter(item => item.status === 'waiting').length;
    const bought = demoItems.filter(item => item.status === 'bought').length;
    const skipped = demoItems.filter(item => item.status === 'skipped').length;
    return { waiting, bought, skipped };
  };

  const calculateTotal = (status: 'bought' | 'skipped') => {
    return demoItems
      .filter(item => item.status === status)
      .reduce((total, item) => total + item.price, 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation - identisch zum echten Dashboard */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center min-h-[64px]">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              onClick={(e) => { 
                e.preventDefault(); 
                navigate('/');
              }} 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-7 h-7 sm:w-8 sm:h-8 -translate-y-0.5"
              />
              <span className="hidden sm:block text-lg sm:text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 bg-clip-text text-transparent group-hover:from-emerald-400 group-hover:via-emerald-500 group-hover:to-emerald-600 transition-all duration-300">Pause</span>
              </span>
            </a>
          </div>

          {/* Begrüßung - zentriert */}
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            <div className="px-3 py-2 bg-emerald-50 text-emerald-600 rounded-lg font-medium flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              Demo Dashboard
            </div>
          </div>

          {/* Rechte Seite */}
          <div className="flex items-center gap-3 ml-auto">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              onClick={() => navigate('/auth')}
            >
              Jetzt registrieren
            </Button>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-4 pt-20">
        {/* Demo-Hinweis */}
        <div className="mb-6 p-4 bg-blue-50/60 backdrop-blur-sm border border-blue-200/60 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <PlayCircle className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-blue-900 mb-1">🚀 Demo-Version - Neues Design</h3>
              <p className="text-blue-700 text-sm leading-relaxed">
                Dies ist eine Demo mit Beispieldaten. Das Design kann sich aufgrund laufender Anpassungen im echten Live-Dashboard unterscheiden. Registrieren Sie sich für Ihr persönliches Dashboard!
              </p>
            </div>
          </div>
        </div>

        {/* Statistik-Karten im neuen Design */}
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('waiting')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">In Warteliste</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().waiting}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('bought')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Gekauft</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().bought}</p>
                  <p className="text-xs text-gray-500">{Math.round(calculateTotal('bought'))}€ ausgegeben</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors duration-300">
                  <CheckCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('skipped')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Impulskauf vermieden</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().skipped}</p>
                  <p className="text-xs text-emerald-600 font-medium">{Math.round(calculateTotal('skipped'))}€ gespart</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors duration-300">
                  <XCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-emerald-700 font-medium">💰 Gesamt Ersparnis</p>
                  <p className="text-3xl font-light text-emerald-900">{calculateTotal('skipped')}€</p>
                  <p className="text-xs text-emerald-600">Dein Erfolg</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-300">
                  <TrendingUp className="w-6 h-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sparziel-Rechner - wie im echten Dashboard */}
        <div className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200/60 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-medium text-emerald-900">Sparziel-Rechner</h3>
              <p className="text-sm text-emerald-700">Verfolge deinen Fortschritt zu deinem Sparziel</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium text-emerald-900">{calculateTotal('skipped')}€ gespart</span>
              <span className="text-emerald-600">→</span>
              <span className="text-lg font-medium text-emerald-900">Urlaub (2.500€)</span>
            </div>
            <div className="text-right">
              <div className="text-sm text-emerald-700 mb-1">0% erreicht</div>
              <div className="text-xs text-emerald-600">Nicht berechenbar</div>
            </div>
          </div>
          
          <div className="mt-4">
            <div className="w-full bg-emerald-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                style={{ width: `${Math.min((calculateTotal('skipped') / 2500) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Hauptinhalt */}
        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div>
                <CardTitle className="text-xl font-light text-gray-900">Aktuelle Warteliste</CardTitle>
                <CardDescription className="text-gray-600 mt-1">
                  Produkte in der 30-Tage Wartezeit
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <Input
                    placeholder="Produkt oder Kategorie suchen..."
                    className="pl-10 w-full sm:w-64 bg-white border-gray-200 focus:bg-white focus:border-emerald-300 focus:ring-emerald-200/50 transition-all duration-200 rounded-xl"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-10 w-10 p-0 bg-white border-gray-200 hover:bg-gray-50 hover:border-emerald-300 transition-all duration-200 rounded-xl ${
                          categoryFilter !== 'all' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''
                        }`}
                        title="Kategorie filtern"
                      >
                        <Filter className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200 rounded-xl shadow-lg">
                      <DropdownMenuItem 
                        onClick={() => setCategoryFilter('all')}
                        className={`rounded-lg ${categoryFilter === 'all' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>🔍</span>
                          <span>Alle Kategorien</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setCategoryFilter('Business')}
                        className={`rounded-lg ${categoryFilter === 'Business' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>💼</span>
                          <span>Business</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setCategoryFilter('Nice-to-Have')}
                        className={`rounded-lg ${categoryFilter === 'Nice-to-Have' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>🎯</span>
                          <span>Nice-to-Have</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setCategoryFilter('Persönlich')}
                        className={`rounded-lg ${categoryFilter === 'Persönlich' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>🏠</span>
                          <span>Persönlich</span>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    onClick={() => navigate('/auth')}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden group flex-1 sm:flex-none rounded-xl"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <Plus className="w-4 h-4 sm:mr-2 relative z-10" />
                    <span className="relative z-10 hidden sm:inline">Neues Produkt</span>
                    <span className="relative z-10 sm:hidden">Neu</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-0">
            <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className="mb-6">
              {/* Desktop: Grid Layout */}
              <div className="hidden sm:block">
                <TabsList className="grid w-full grid-cols-5 bg-gray-50 border border-gray-200 p-1 rounded-xl">
                  <TabsTrigger 
                    value="all" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Alle
                  </TabsTrigger>
                  <TabsTrigger 
                    value="waiting" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Warteliste
                  </TabsTrigger>
                  <TabsTrigger 
                    value="bought" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Gekauft
                  </TabsTrigger>
                  <TabsTrigger 
                    value="skipped" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Impulskauf vermieden
                  </TabsTrigger>
                  <TabsTrigger 
                    value="subscriptions" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Abos
                  </TabsTrigger>
                </TabsList>
              </div>
              
              {/* Mobile: Scrollbare Tab-Liste */}
              <div className="relative sm:hidden">
                <div className="overflow-x-auto scrollbar-hide">
                  <TabsList className="flex w-max min-w-full bg-gray-50 border border-gray-200 p-1 rounded-xl">
                    <TabsTrigger 
                      value="all" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Alle
                    </TabsTrigger>
                    <TabsTrigger 
                      value="waiting" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Warteliste
                    </TabsTrigger>
                    <TabsTrigger 
                      value="bought" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Gekauft
                    </TabsTrigger>
                    <TabsTrigger 
                      value="skipped" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Vermieden
                    </TabsTrigger>
                    <TabsTrigger 
                      value="subscriptions" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Abos
                    </TabsTrigger>
                  </TabsList>
                </div>
              </div>
            </Tabs>

            {/* Abos Tab Content */}
            {activeTab === 'subscriptions' && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Abonnements</h3>
                <p className="text-gray-600 mb-6">
                  In der Demo-Version sind keine Abonnements verfügbar. 
                  Registrieren Sie sich für die vollständige Abo-Verwaltung.
                </p>
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Jetzt registrieren
                </Button>
              </div>
            )}

            {/* Produktliste für andere Tabs */}
            {activeTab !== 'subscriptions' && (
              <div className="space-y-4">
                {filteredItems.map((item, index) => (
                <Card key={item.id} className="group bg-white/60 backdrop-blur-sm border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-gray-900 group-hover:text-gray-700 transition-colors">
                            {item.name}
                          </h3>
                          <Badge variant="secondary" className="text-xs">
                            {item.category}
                          </Badge>
                        </div>
                        <p className="text-2xl font-light text-gray-900 mb-1">{item.price}€</p>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Link className="w-4 h-4 mr-2" />
                            Link öffnen
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <XCircle className="w-4 h-4 mr-2" />
                            Entfernen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Wartezeit</span>
                        <span className={`font-medium ${
                          formatTimeLeft(item.created_at) === 'Wartezeit beendet' 
                            ? 'text-emerald-600' 
                            : 'text-blue-600'
                        }`}>
                          {formatTimeLeft(item.created_at)}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              getProgressPercentage(item.created_at) >= 100 
                                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                                : 'bg-gradient-to-r from-blue-400 to-blue-500'
                            }`}
                            style={{ width: `${getProgressPercentage(item.created_at)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Tag 1</span>
                          <span>Tag 30</span>
                        </div>
                      </div>
                      
                      {/* Aktions-Buttons nur für Warteliste-Items */}
                      {item.status === 'waiting' && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                          <Button
                            size="sm"
                            variant="outline"
                                                         onClick={() => handleStatusChange(item.id.toString(), 'skipped')}
                            className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Impulskauf vermieden
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                                                         onClick={() => handleStatusChange(item.id.toString(), 'bought')}
                            className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Gekauft
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Call to Action */}
        <div className="mt-8 text-center">
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/60">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-emerald-900 mb-4">
                Bereit für Ihr persönliches Dashboard?
              </h3>
              <p className="text-emerald-700 mb-6 max-w-2xl mx-auto">
                Erstellen Sie jetzt Ihr kostenloses Konto und beginnen Sie mit bewusstem Konsumieren. 
                Keine versteckten Kosten, keine Verpflichtungen.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 rounded-xl"
                onClick={() => navigate('/auth')}
              >
                Jetzt kostenloses Konto erstellen
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Demo; 